import * as Yup from "yup";

import { TextManager } from "Components/TextContext";
import { showIsCarTrimmed } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/helpers";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { AutoRentalTypeVariable } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseCarProduct";
import { CarCoverage } from "Store/interfaces/products/coverage";
import { UserIdentificationData } from "Store/userIdentification";
import { CarOwner } from "../ProductDetails/FormElements/interfaces";
import { CarPriceFormValues } from "./interfaces";

const CAR_OWNER_AGE_LOWER_BOUNDARY = 18;
const CAR_OWNER_AGE_UPPER_BOUNDARY = 99;

export const validationSchema = (
  brand: string,
  year: string,
  userIdentification: UserIdentificationData | null,
): Yup.ObjectSchema<Omit<CarPriceFormValues, "tfaAmount">> => {
  const { getText } = TextManager;
  return priceValidationSchema().concat(
    Yup.object().shape({
      isCarTrimmed: Yup.mixed<BooleanFormType>().when(["usedByYoungerDriver"], {
        is: (usedByYoungerDriver: BooleanFormType) =>
          showIsCarTrimmed(
            brand,
            year,
            userIdentification,
            usedByYoungerDriver,
          ),
        then: (schema) =>
          schema
            .oneOf(
              [BooleanFormType.FALSE],
              getText("carform.validation.iscartrimmed"),
            )
            .required(getText("carform.validation.iscartrimmed-required")),
      }),
    }),
  );
};

export const priceValidationSchema = (): Yup.ObjectSchema<
  Omit<CarPriceFormValues, "tfaAmount" | "isCarTrimmed">
> => {
  const { getText } = TextManager;
  return Yup.object()
    .defined()
    .shape({
      yearlyKmNeed: Yup.string().required(
        getText("carform.validation.yearlykmneed"),
      ),
      usedByYoungerDriver: Yup.mixed<BooleanFormType>().required(),
      coverage: Yup.mixed<CarCoverage>().required(
        getText("checkprice.common.validation.coverage-required"),
      ),
      rentalType: Yup.mixed<AutoRentalTypeVariable>().required(),
      deductible: Yup.string().when("coverage", {
        is: (coverage: CarCoverage) =>
          coverage !== CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT,
        then: (schema) => schema.required(),
      }),
      carOwner: Yup.mixed<CarOwner>()
        .required(getText("carform.validation.car_owner-required"))
        .notOneOf([CarOwner.OTHER, CarOwner.UNSELECTED], ""),
      carOwnerAge: Yup.number().when("carOwner", {
        is: (carOwner: CarOwner) =>
          carOwner === CarOwner.PARTNER || carOwner === CarOwner.GUARDIAN,
        then: (schema) =>
          schema
            .required(getText("carform.validation.car_owner_age-required"))
            .min(
              CAR_OWNER_AGE_LOWER_BOUNDARY,
              getText("carform.validation.car_owner_age-min"),
            )
            .max(
              CAR_OWNER_AGE_UPPER_BOUNDARY,
              getText("carform.validation.car_owner_age-max"),
            ),
      }),
    });
};
